import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, SwordsIcon, ZapIcon, ClockIcon, HandIcon, CrosshairIcon, ChevronLeftIcon } from 'lucide-react';
interface GameModeSelectorProps {
  onSelect: (mode: string) => void;
}
export function GameModeSelector({
  onSelect
}: GameModeSelectorProps) {
  const [showChallenges, setShowChallenges] = useState(false);
  const modes = [{
    id: 'stroke',
    title: 'Stroke Play',
    desc: 'Classic scoring. Lowest total wins.',
    icon: TargetIcon,
    color: 'text-brand-gold',
    bg: 'bg-brand-gold/10',
    border: 'border-brand-gold/30',
    badge: '3 holes'
  }, {
    id: 'match',
    title: 'Match Play',
    desc: 'Head to head. Win holes, win the match.',
    icon: SwordsIcon,
    color: 'text-brand-cream-light',
    bg: 'bg-brand-cream/10',
    border: 'border-brand-cream/20',
    badge: '2 players'
  }, {
    id: 'challenge',
    title: 'Challenge Mode',
    desc: 'Fun challenges to test your skills.',
    icon: ZapIcon,
    color: 'text-brand-sand',
    bg: 'bg-brand-sand/10',
    border: 'border-brand-sand/30',
    badge: '3 challenges'
  }];
  const challenges = [{
    id: 'challenge-speed',
    title: 'Speed Hole',
    desc: 'Race the clock. Sink it fast for bonus points.',
    icon: ClockIcon,
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/30',
    badge: 'Timed'
  }, {
    id: 'challenge-wronghand',
    title: 'Wrong Handed',
    desc: 'Putt with your non-dominant hand. Double points.',
    icon: HandIcon,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    badge: '2x Points'
  }, {
    id: 'challenge-closest',
    title: 'Closest to the Pin',
    desc: 'Precision putting. Closest to the hole wins.',
    icon: CrosshairIcon,
    color: 'text-brand-green-light',
    bg: 'bg-brand-green-light/20',
    border: 'border-brand-green-light/40',
    badge: 'Precision'
  }];
  const handleModeClick = (id: string) => {
    if (id === 'challenge') {
      setShowChallenges(true);
    } else {
      onSelect(id);
    }
  };
  return <div className="flex flex-col h-full p-6 bg-brand-charcoal relative" data-id="element-155">
      <AnimatePresence mode="wait" data-id="element-156">
        {!showChallenges ? <motion.div key="modes" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} className="flex flex-col h-full" data-id="element-157">
            <div className="mt-8 mb-8" data-id="element-158">
              <h2 className="text-3xl font-heading text-brand-cream-light mb-2" data-id="element-159">
                Select Format
              </h2>
              <p className="text-brand-cream/60 font-body text-sm" data-id="element-160">
                Choose how you want to play today.
              </p>
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto pb-8" data-id="element-161">
              {modes.map((mode, i) => <motion.button key={mode.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} onClick={() => handleModeClick(mode.id)} className={`w-full text-left p-5 rounded-2xl border ${mode.border} ${mode.bg} backdrop-blur-sm hover:bg-brand-cream/10 transition-all active:scale-[0.98] group relative overflow-hidden`} data-id="element-162">
                  <div className="flex items-start space-x-4 relative z-10" data-id="element-163">
                    <div className={`p-3 rounded-xl bg-brand-charcoal/50 ${mode.color}`} data-id="element-164">
                      <mode.icon className="w-6 h-6" data-id="element-165" />
                    </div>
                    <div className="flex-1" data-id="element-166">
                      <div className="flex items-center justify-between mb-1" data-id="element-167">
                        <h3 className="text-xl font-heading text-brand-cream-light group-hover:text-brand-gold transition-colors" data-id="element-168">
                          {mode.title}
                        </h3>
                        <span className="text-[10px] uppercase tracking-widest font-bold bg-brand-charcoal/50 px-2 py-1 rounded-md text-brand-cream/70" data-id="element-169">
                          {mode.badge}
                        </span>
                      </div>
                      <p className="text-brand-cream/60 font-body text-xs leading-relaxed pr-4" data-id="element-170">
                        {mode.desc}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-cream/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" data-id="element-171" />
                </motion.button>)}
            </div>
          </motion.div> : <motion.div key="challenges" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 20
      }} className="flex flex-col h-full" data-id="element-172">
            <div className="mt-4 mb-6" data-id="element-173">
              <button onClick={() => setShowChallenges(false)} className="flex items-center text-brand-cream/50 hover:text-brand-cream-light mb-4 transition-colors" data-id="element-174">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-175" />
                <span className="text-sm" data-id="element-176">Back to Modes</span>
              </button>
              <h2 className="text-3xl font-heading text-brand-cream-light mb-2" data-id="element-177">
                Challenge Mode
              </h2>
              <p className="text-brand-cream/60 font-body text-sm" data-id="element-178">
                Select your challenge type.
              </p>
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto pb-8" data-id="element-179">
              {challenges.map((challenge, i) => <motion.button key={challenge.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} onClick={() => onSelect(challenge.id)} className={`w-full text-left p-5 rounded-2xl border ${challenge.border} ${challenge.bg} backdrop-blur-sm hover:bg-brand-cream/10 transition-all active:scale-[0.98] group relative overflow-hidden`} data-id="element-180">
                  <div className="flex items-start space-x-4 relative z-10" data-id="element-181">
                    <div className={`p-3 rounded-xl bg-brand-charcoal/50 ${challenge.color}`} data-id="element-182">
                      <challenge.icon className="w-6 h-6" data-id="element-183" />
                    </div>
                    <div className="flex-1" data-id="element-184">
                      <div className="flex items-center justify-between mb-1" data-id="element-185">
                        <h3 className="text-xl font-heading text-brand-cream-light group-hover:text-brand-gold transition-colors" data-id="element-186">
                          {challenge.title}
                        </h3>
                        <span className="text-[10px] uppercase tracking-widest font-bold bg-brand-charcoal/50 px-2 py-1 rounded-md text-brand-cream/70" data-id="element-187">
                          {challenge.badge}
                        </span>
                      </div>
                      <p className="text-brand-cream/60 font-body text-xs leading-relaxed pr-4" data-id="element-188">
                        {challenge.desc}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-cream/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" data-id="element-189" />
                </motion.button>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}